// file: src/utils/mobileOptimizations.js

/**
 * Mobile performance optimization utilities
 * Helps prevent crashes and improve performance on mobile devices
 */

// Detect if we're on a mobile device
export function isMobileDevice() {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

// Detect if device has limited memory (< 4GB)
export function hasLimitedMemory() {
  // Check if the device memory API is available
  if ('deviceMemory' in navigator) {
    return navigator.deviceMemory < 4;
  }
  // Assume limited memory on mobile devices
  return isMobileDevice();
}

// Throttle function for scroll events (more aggressive than debounce)
export function throttle(func, delay) {
  let lastCall = 0;
  let timeoutId = null;
  
  return function throttled(...args) {
    const now = Date.now();
    const timeSinceLastCall = now - lastCall;
    
    if (timeSinceLastCall >= delay) {
      lastCall = now;
      func.apply(this, args);
    } else {
      // Schedule the function to run after the delay
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        lastCall = Date.now();
        func.apply(this, args);
      }, delay - timeSinceLastCall);
    }
  };
}

// Request Idle Callback polyfill for older browsers
export function requestIdleCallback(callback, options) {
  if ('requestIdleCallback' in window) {
    return window.requestIdleCallback(callback, options);
  }
  // Fallback for browsers without requestIdleCallback
  const start = Date.now();
  return setTimeout(() => {
    callback({
      didTimeout: false,
      timeRemaining: () => Math.max(0, 50 - (Date.now() - start))
    });
  }, 1);
}

export function cancelIdleCallback(id) {
  if ('cancelIdleCallback' in window) {
    return window.cancelIdleCallback(id);
  }
  clearTimeout(id);
}

// Safe IndexedDB wrapper that won't block on mobile
export async function safeIndexedDBWrite(operation) {
  if (!operation || typeof operation !== 'function') {
    console.error('[SafeDB] Invalid operation provided');
    return;
  }

  try {
    // On mobile with limited memory, use requestIdleCallback
    if (hasLimitedMemory()) {
      return new Promise((resolve, reject) => {
        requestIdleCallback(async () => {
          try {
            const result = await operation();
            resolve(result);
          } catch (err) {
            reject(err);
          }
        }, { timeout: 2000 });
      });
    } else {
      // On desktop, execute immediately but still async
      return await operation();
    }
  } catch (err) {
    console.error('[SafeDB] IndexedDB write failed:', err);
    // Don't throw - just log the error to prevent app crashes
    return null;
  }
}

// Visibility change handler to pause expensive operations when tab is hidden
let visibilityCallbacks = [];

export function onVisibilityChange(callback) {
  if (typeof callback !== 'function') return;
  
  visibilityCallbacks.push(callback);
  
  // Return cleanup function
  return () => {
    visibilityCallbacks = visibilityCallbacks.filter(cb => cb !== callback);
  };
}

// Initialize visibility change listener
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    const isHidden = document.hidden;
    visibilityCallbacks.forEach(callback => {
      try {
        callback(isHidden);
      } catch (err) {
        console.error('[Visibility] Callback error:', err);
      }
    });
  });
}

// Get current visibility state
export function isPageHidden() {
  return typeof document !== 'undefined' && document.hidden;
}

// Memory pressure detection and handling
export function addMemoryPressureListener(callback) {
  if (!('memory' in performance)) {
    // Memory API not available, skip
    return () => {};
  }

  const checkMemory = () => {
    const memory = performance.memory;
    const usedMemoryRatio = memory.usedJSHeapSize / memory.jsHeapSizeLimit;
    
    // If using more than 80% of available heap, trigger callback
    if (usedMemoryRatio > 0.8) {
      callback(usedMemoryRatio);
    }
  };

  // Check every 30 seconds
  const intervalId = setInterval(checkMemory, 30000);
  
  return () => clearInterval(intervalId);
}

// Safe scroll handler that won't crash on mobile
export function createSafeScrollHandler(handler, delay = 100) {
  let rafId = null;
  let lastScrollTime = 0;
  
  return (event) => {
    const now = Date.now();
    
    // Cancel any pending animation frame
    if (rafId) {
      cancelAnimationFrame(rafId);
    }
    
    // Throttle to prevent excessive calls on mobile
    if (now - lastScrollTime < delay) {
      return;
    }
    
    lastScrollTime = now;
    
    // Use requestAnimationFrame for smooth performance
    rafId = requestAnimationFrame(() => {
      try {
        handler(event);
      } catch (err) {
        console.error('[SafeScroll] Handler error:', err);
      }
      rafId = null;
    });
  };
}

// Cleanup utility to prevent memory leaks
export function createCleanupManager() {
  const cleanupFunctions = [];
  
  return {
    add: (cleanup) => {
      if (typeof cleanup === 'function') {
        cleanupFunctions.push(cleanup);
      }
    },
    cleanup: () => {
      cleanupFunctions.forEach(fn => {
        try {
          fn();
        } catch (err) {
          console.error('[Cleanup] Error during cleanup:', err);
        }
      });
      cleanupFunctions.length = 0;
    }
  };
}

